/* ---------------------------------------------------------------------------
   What a road code says the road is.

   A leaf module on purpose: it imports the contract and nothing else, takes its
   rules as an argument, and holds no state. Everything about a jurisdiction's
   classification is in the rules its profile carries, so this file never needs
   editing when a department joins — and a profile can build its own groupings
   out of `classifyRoad` without importing the registry that imports it.

   AN UNMATCHED CODE CLASSIFIES AS NOTHING. Not as "district road", not as
   "other", not as the empty string. The register imports from spreadsheets and a
   prefix nobody has seen before is a thing to notice rather than to quietly
   bucket: `null` is what makes it show up in the summary as *not recorded*
   instead of inflating a real class's count by one.
   --------------------------------------------------------------------------- */

import type { RoadClassRule } from './types.ts';

/**
 * The class of a road code under one jurisdiction's rules, or null.
 *
 * Trimmed and upper-cased before matching, because a route arrives off a
 * spreadsheet as readily as off a form and `f045` is the same road as `F045`.
 * First rule that matches wins, so the order a profile lists them in is part of
 * the rules — see the header of src/tenant/profiles/nepal-dor.ts, where two of
 * the four prefixes overlap.
 */
export function classifyRoad(
  route: string | null | undefined,
  rules: readonly RoadClassRule[],
): string | null {
  const code = (route ?? '').trim().toUpperCase();
  if (!code) return null;
  for (const rule of rules) if (rule.match.test(code)) return rule.label;
  return null;
}

/** Every class a jurisdiction can produce, in the order its profile lists them. */
export function roadClassLabels(rules: readonly RoadClassRule[]): string[] {
  return rules.map((r) => r.label);
}
